import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Save, Plus, Trash2, Loader2, ArrowLeft } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { voucherService } from '@/services/voucherService'
import { financeService } from '@/services/financeService'
import { useTranslation } from 'react-i18next'
import {
  Voucher,
  VoucherLine,
  VoucherType,
} from '@/api/types'

export const GeneralEntriesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const isReadOnly = location.pathname.includes('/view') || (location.state as any)?.readOnly === true
  const branchId = user?.selectedBranch?.branchId
  // companyId removed - not currently used

  const getDefaultRows = (): VoucherLine[] => {
    return Array.from({ length: 3 }, (): VoucherLine => ({
      lineId: 0,
      voucherId: 0,
      accountId: 0,
      employeeId: 0,
      description: '',
      debit: 0,
      credit: 0,
      projectId: 0,
      createdDate: new Date().toISOString(),
      createdBy: 0,
      modifiedDate: new Date().toISOString(),
      modifiedBy: 0,
      reportShowStatus: true,
      date: new Date().toISOString().split('T')[0],
      department: '',
      costProfitCenterId: 0,
      costLocationId: 0,
      isDeleted: '',
      branchId: branchId || 0,
    }))
  }

  const [voucher, setVoucher] = useState<Voucher>({
    voucherId: 0,
    refNo: '',
    refType: '', // Default to empty string (None) for new entries
    voucherEntryDate: new Date().toISOString().split('T')[0],
    voucherCode: '',
    currency: '',
    totalDebit: 0,
    totalCredit: 0,
    description: '',
    createdDate: new Date().toISOString(),
    createdBy: user?.userId || 0,
    modifiedDate: new Date().toISOString(),
    modifiedBy: 0,
    voucherType: 0,
    customerId: 0,
    vendorId: 0,
    branchId: branchId || 0,
    file: '',
    department: '',
    attachmentFile: null,
    voucherLines: getDefaultRows(),
  })

  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [projects, setProjects] = useState<any[]>([])
  const [profitCenters, setProfitCenters] = useState<any[]>([])
  const [costLocations, setCostLocations] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [vendors, setVendors] = useState<any[]>([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  // Load lookup data
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [
          typesData,
          accountsData,
          projectsData,
          profitCentersData,
          costLocationsData,
          customersData,
          vendorsData,
        ] = await Promise.all([
          voucherService.getVoucherTypes(),
          financeService.getAllAccountsForVoucher(branchId),
          financeService.getProjectsForVoucher(branchId),
          financeService.getProfitCenters(branchId),
          financeService.getCostLocations(branchId),
          financeService.getAllCustomers(branchId),
          financeService.getAllVendorsForVoucher(branchId),
        ])

        console.log('🔵 [GeneralEntry] Loaded data:', {
          types: typesData.length,
          accounts: accountsData.length,
          projects: projectsData.length,
          profitCenters: profitCentersData.length,
          costLocations: costLocationsData.length,
          customers: customersData.length,
          vendors: vendorsData.length,
        })

        setVoucherTypes(typesData)
        setAccounts(accountsData)
        setProjects(projectsData)
        setProfitCenters(profitCentersData)
        setCostLocations(costLocationsData)
        setCustomers(customersData)
        setVendors(vendorsData)
      } catch (error: any) {
        console.error('❌ [GeneralEntry] Failed to load lookups', error)
        console.error('❌ [GeneralEntry] Error response:', error.response)
        console.error('❌ [GeneralEntry] Error data:', error.response?.data)
        await showError('Error', error.response?.data?.message || t('generalEntries.failedToLoadGeneralEntries'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load voucher data for edit or view
  useEffect(() => {
    if ((!isEditMode && !isReadOnly) || !branchId || !id) return

    const loadVoucher = async () => {
      try {
        setLoading(true)
        const voucherId = Number(id)
        const voucherData = await voucherService.getVoucherById(branchId, voucherId)
        const lines = await voucherService.getVoucherLinesByVoucherId(branchId, voucherId)

        const formatDateForInput = (dateString?: string) => {
          if (!dateString) return ''
          try {
            if (dateString.includes('T')) {
              return new Date(dateString).toISOString().split('T')[0]
            }
            return dateString.split('T')[0]
          } catch {
            return dateString
          }
        }

        // Mark lines that are already deleted
        const deletedIndices = new Set<number>()
        lines.forEach((line, index) => {
          if (line.isDeleted === 'true' || (typeof line.isDeleted === 'boolean' && line.isDeleted === true)) {
            deletedIndices.add(index)
          }
        })

        setVoucher({
          ...voucherData,
          refType: voucherData.refType || '', // Keep existing refType or default to empty string (None)
          voucherEntryDate: formatDateForInput(voucherData.voucherEntryDate) || new Date().toISOString().split('T')[0],
          voucherLines: lines.length > 0 ? lines : getDefaultRows(),
        })
        
        setDeletedRowIndices(deletedIndices)
      } catch (error: any) {
        console.error('Failed to load voucher', error)
        await showError('Error', error.response?.data?.message || t('generalEntries.failedToLoadGeneralEntry'))
      } finally {
        setLoading(false)
      }
    }

    loadVoucher()
  }, [isEditMode, isReadOnly, branchId, id])

  // Calculate totals
  const totals = useMemo(() => {
    const validLines = voucher.voucherLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const totalDebit = validLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0)
    const totalCredit = validLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0)
    return { totalDebit, totalCredit, difference: totalDebit - totalCredit }
  }, [voucher.voucherLines, deletedRowIndices])

  useEffect(() => {
    setVoucher((prev) => ({
      ...prev,
      totalDebit: totals.totalDebit,
      totalCredit: totals.totalCredit,
    }))
  }, [totals])

  // Form validation
  const isFormValid = useMemo(() => {
    if (!voucher.voucherEntryDate) return false
    if (!voucher.voucherType || voucher.voucherType === 0) return false
    if (totals.totalDebit !== totals.totalCredit) return false
    return true
  }, [voucher.voucherEntryDate, voucher.voucherType, totals])

  const handleLineChange = (index: number, field: keyof VoucherLine, value: any) => {
    setVoucher((prev) => {
      const newLines = [...(prev.voucherLines || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      // When account is selected, update account title
      if (field === 'accountId' && value) {
        const account = accounts.find((a) => (a.id || a.accountId) === Number(value))
        if (account) {
          // Account title is displayed but not stored separately
        }
      }

      newLines[index] = updatedLine
      return { ...prev, voucherLines: newLines }
    })
  }

  const handleAddRows = (count: number) => {
    setVoucher((prev) => {
      const newLines = Array.from({ length: count }, (): VoucherLine => ({
        lineId: 0,
        voucherId: prev.voucherId || 0,
        accountId: 0,
        employeeId: 0,
        description: '',
        debit: 0,
        credit: 0,
        projectId: 0,
        createdDate: new Date().toISOString(),
        createdBy: 0,
        modifiedDate: new Date().toISOString(),
        modifiedBy: 0,
        reportShowStatus: true,
        date: new Date().toISOString().split('T')[0],
        department: '',
        costProfitCenterId: 0,
        costLocationId: 0,
        isDeleted: '',
        branchId: branchId || 0,
      }))
      return { ...prev, voucherLines: [...(prev.voucherLines || []), ...newLines] }
    })
  }

  const handleDeleteRow = (index: number) => {
    setDeletedRowIndices((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Store both file name and file object
      setVoucher((prev) => ({ 
        ...prev, 
        file: file.name,
        attachmentFile: file,
      }))
    }
  }

  /**
   * Get user IP address (logged-in user's IP, not server's IP)
   */
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('generalEntries.pleaseSelectBranchFirst'))
      return
    }

    if (!voucher.voucherEntryDate) {
      await showValidationError(t('generalEntries.generalEntryDateRequired'))
      return
    }

    if (!voucher.voucherType || voucher.voucherType === 0) {
      await showValidationError(t('generalEntries.generalEntryTypeRequired'))
      return
    }

    if (totals.totalDebit !== totals.totalCredit) {
      await showValidationError(t('generalEntries.totalDebitMustEqualCredit', { debit: totals.totalDebit.toFixed(2), credit: totals.totalCredit.toFixed(2) }))
      return
    }

    const validLines = voucher.voucherLines?.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      return !isDeleted && line.accountId && line.accountId > 0
    }) || []

    if (validLines.length === 0) {
      await showValidationError(t('generalEntries.pleaseAddAtLeastOneTransactionLine'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating General Entry...' : 'Saving General Entry...',
        'Please wait while we process your request'
      )
      
      const currentBranchId = user?.selectedBranch?.branchId
      if (!currentBranchId) {
        closeSwal()
        await showValidationError(t('generalEntries.branchIdRequired'))
        setSaving(false)
        return
      }

      const linesToSave: VoucherLine[] = []
      voucher.voucherLines?.forEach((line, index) => {
        const isDeleted = deletedRowIndices.has(index)
        
        // For new entries, only save non-deleted lines with account
        if (!isEditMode) {
          if (!isDeleted && line.accountId && line.accountId > 0) {
            linesToSave.push({
              ...line,
              voucherId: voucher.voucherId || 0,
              branchId: currentBranchId,
              createdBy: user?.userId || 0,
              modifiedBy: 0,
              createdDate: new Date().toISOString(),
              modifiedDate: new Date().toISOString(),
              date: line.date || voucher.voucherEntryDate,
              isDeleted: '',
            })
          }
        } else {
          // For updates, include all lines (deleted and non-deleted) so API can handle deletions
          if (isDeleted || (line.accountId && line.accountId > 0)) {
            linesToSave.push({
              ...line,
              voucherId: voucher.voucherId || 0,
              branchId: currentBranchId,
              createdBy: line.createdBy || user?.userId || 0,
              modifiedBy: isEditMode ? (user?.userId || 0) : 0,
              createdDate: line.createdDate || new Date().toISOString(),
              modifiedDate: new Date().toISOString(),
              date: line.date || voucher.voucherEntryDate,
              isDeleted: isDeleted ? 'true' : '',
            })
          }
        }
      })

      // Get IP address
      const ipAddress = await getUserIPAddress()

      const voucherToSave: Voucher = {
        ...voucher,
        refType: voucher.refType || '', // Keep refType as is (can be empty string for None)
        branchId: currentBranchId,
        totalDebit: totals.totalDebit,
        totalCredit: totals.totalCredit,
        voucherLines: linesToSave,
        createdBy: isEditMode ? voucher.createdBy : (user?.userId || 0),
        modifiedBy: isEditMode ? (user?.userId || 0) : 0,
        createdDate: isEditMode ? voucher.createdDate : new Date().toISOString(),
        modifiedDate: new Date().toISOString(),
        ipAddress: ipAddress,
      }

      // Debug logging (development only)
      if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === 'true') {
        console.group('%c📦 GENERAL ENTRY PAYLOAD', 'background: #2196F3; color: white; padding: 10px; font-weight: bold; font-size: 16px; border-radius: 5px;')
        console.log('%c🔄 Mode:', 'color: #4CAF50; font-weight: bold; font-size: 14px;', isEditMode ? 'UPDATE' : 'CREATE')
        console.log('%c🌐 IP Address:', 'color: #9C27B0; font-weight: bold; font-size: 14px;', ipAddress)
        console.log('%c📋 Complete Voucher Object:', 'color: #FF9800; font-weight: bold; font-size: 14px;')
        console.table({
          'VoucherId': voucherToSave.voucherId,
          'RefNo': voucherToSave.refNo,
          'RefType': voucherToSave.refType,
          'VoucherEntryDate': voucherToSave.voucherEntryDate,
          'VoucherCode': voucherToSave.voucherCode,
          'Currency': voucherToSave.currency,
          'TotalDebit': voucherToSave.totalDebit,
          'TotalCredit': voucherToSave.totalCredit,
          'Description': voucherToSave.description,
          'CreatedDate': voucherToSave.createdDate,
          'CreatedBy': voucherToSave.createdBy,
          'ModifiedDate': voucherToSave.modifiedDate,
          'ModifiedBy': voucherToSave.modifiedBy,
          'VoucherType': voucherToSave.voucherType,
          'CustomerId': voucherToSave.customerId,
          'VendorId': voucherToSave.vendorId,
          'BranchId': voucherToSave.branchId,
          'File': voucherToSave.file,
          'Department': voucherToSave.department,
          'IpAddress': voucherToSave.ipAddress,
          'HasAttachmentFile': !!voucherToSave.attachmentFile,
        })
        console.log('%c📊 Voucher Lines (' + linesToSave.length + ' lines):', 'color: #F44336; font-weight: bold; font-size: 14px;')
        console.table(linesToSave.map((line, idx) => ({
          'Index': idx + 1,
          'LineId': line.lineId,
          'VoucherId': line.voucherId,
          'AccountId': line.accountId,
          'Description': line.description,
          'Debit': line.debit,
          'Credit': line.credit,
          'ProjectId': line.projectId,
          'Date': line.date,
          'Department': line.department,
          'CostProfitCenterId': line.costProfitCenterId,
          'CostLocationId': line.costLocationId,
          'IsDeleted': line.isDeleted,
          'BranchId': line.branchId,
        })))
        console.log('%c📄 Full JSON Payload:', 'color: #E91E63; font-weight: bold; font-size: 14px;')
        console.log(JSON.stringify(voucherToSave, null, 2))
        console.log('%c📦 Raw Voucher Object:', 'color: #00BCD4; font-weight: bold; font-size: 14px;')
        console.log(voucherToSave)
        console.groupEnd()
      }

      if (isEditMode) {
        await voucherService.updateVoucher(voucherToSave, ipAddress)
      } else {
        await voucherService.createVoucher(voucherToSave, ipAddress)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('generalEntries.generalEntryUpdatedSuccess') : t('generalEntries.generalEntryCreatedSuccess')
      )

      navigate('/general-entries')
    } catch (error: any) {
      // Error logging (always log errors, but detailed only in development)
      console.error('Failed to save voucher:', error.message)
      
      if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === 'true') {
        console.group('%c❌ GENERAL ENTRY SAVE ERROR', 'background: #F44336; color: white; padding: 10px; font-weight: bold; font-size: 16px; border-radius: 5px;')
        console.error('%c🚨 Error Message:', 'color: #F44336; font-weight: bold; font-size: 14px;', error.message)
        console.error('%c📋 Error Response Status:', 'color: #9C27B0; font-weight: bold; font-size: 14px;', error.response?.status)
        console.error('%c📄 Error Response Data (Full):', 'color: #E91E63; font-weight: bold; font-size: 14px;')
        console.log(error.response?.data)
        if (error.response?.data) {
          console.log('%c📝 Server Error Message:', 'color: #FF5722; font-weight: bold; font-size: 14px;', error.response.data.message || error.response.data.error || error.response.data.msg || 'No message provided')
          console.log('%c📋 Server Error Details:', 'color: #FF9800; font-weight: bold; font-size: 14px;')
          console.table(error.response.data)
        }
        console.error('%c📡 Error Response Object:', 'color: #FF9800; font-weight: bold; font-size: 14px;', error.response)
        console.error('%c📦 Error Response Headers:', 'color: #00BCD4; font-weight: bold; font-size: 14px;', error.response?.headers)
        console.error('%c🔍 Complete Error Object:', 'color: #2196F3; font-weight: bold; font-size: 14px;', error)
        console.error('%c📊 Error Stack:', 'color: #795548; font-weight: bold; font-size: 14px;', error.stack)
        console.groupEnd()
      } else {
        // Production: log essential error info only
        if (error.response?.data?.message) {
          console.error('Server error:', error.response.data.message)
        }
      }
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const getAccountTitle = (accountId: number): string => {
    const account = accounts.find((a) => (a.id || a.accountId) === accountId)
    return account ? (account.nameEn || account.name || account.accountNameEn || account.accountName || '') : ''
  }

  // getAccountCode function removed - not currently used

  // Prepare options for dropdowns
  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => ({
    value: String(acc.id || acc.accountId || 0),
    label: `${acc.code || acc.accountCode || ''} - ${acc.nameEn || acc.name || acc.accountNameEn || acc.accountName || ''}`,
  }))

  const projectOptions: SearchableSelectOption[] = projects.map((proj) => ({
    value: String(proj.id || proj.projectId || 0),
    label: proj.name || proj.nameEn || proj.projectName || proj.projectNameEn || '',
  }))

  const profitCenterOptions: SearchableSelectOption[] = profitCenters.map((pc) => ({
    value: String(pc.id || 0),
    label: pc.lookupNameEn || pc.nameEn || pc.name || '',
  }))

  const costLocationOptions: SearchableSelectOption[] = costLocations.map((cl) => ({
    value: String(cl.id || 0),
    label: cl.lookupNameEn || cl.nameEn || cl.name || '',
  }))

  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))

  const vendorOptions: SearchableSelectOption[] = vendors.map((vend) => ({
    value: String(vend.vendorId || 0),
    label: vend.vendorNameEn || vend.vendorNameAr || `Vendor ${vend.vendorId}`,
  }))

  const refTypeOptions: SearchableSelectOption[] = [
    { value: '', label: t('generalEntries.none') },
    { value: 'Customer', label: t('generalEntries.customerRef') },
    { value: 'Vendor', label: t('generalEntries.vendorRef') },
  ]

  const voucherTypeOptions: SearchableSelectOption[] = voucherTypes
    .filter((vt) => vt && (vt.id || vt.id === 0) && (vt.name || vt.nameEn || vt.nameAr))
    .map((vt) => ({
      value: String(vt.id || 0),
      label: vt.name || vt.nameEn || vt.nameAr || '',
    }))

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('generalEntries.title')} description={t('generalEntries.createOrEditGeneralEntry')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('generalEntries.title')}
          description={
            isReadOnly 
              ? t('generalEntries.viewGeneralEntryReadOnly')
              : isEditMode 
                ? t('generalEntries.editGeneralEntryDesc')
                : t('generalEntries.createNewGeneralEntryDesc')
          }
        />
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('generalEntries.return')}
        </Button>
      </div>

      {/* General Entry Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('generalEntries.generalEntry')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t('generalEntries.generalEntryDate')} <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={voucher.voucherEntryDate}
                onChange={(e) => setVoucher((prev) => ({ ...prev, voucherEntryDate: e.target.value }))}
                required
                disabled={isReadOnly}
                className={isReadOnly ? 'font-bold' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.generalEntryCode')}</Label>
              <Input
                value={voucher.voucherCode || ''}
                readOnly
                className={`bg-muted ${isReadOnly ? 'font-bold' : ''}`}
                placeholder={t('generalEntries.autoGenerated')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.generalEntryType')} <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={voucherTypeOptions}
                value={voucher.voucherType ? String(voucher.voucherType) : ''}
                onValueChange={(value) => setVoucher((prev) => ({ ...prev, voucherType: Number(value) || 0 }))}
                placeholder="Select type"
                disabled={isReadOnly}
                className={isReadOnly ? 'font-bold' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.refType')}</Label>
              <SearchableSelect
                options={refTypeOptions}
                value={voucher.refType || ''}
                onValueChange={(value) => {
                  const newRefType = value || ''
                  setVoucher((prev) => ({
                    ...prev,
                    refType: newRefType,
                    // Reset customerId/vendorId when None is selected or when switching types
                    customerId: newRefType === 'Customer' ? prev.customerId : 0,
                    vendorId: newRefType === 'Vendor' ? prev.vendorId : 0,
                  }))
                }}
                placeholder={t('generalEntries.selectRefType')}
                disabled={isReadOnly}
                className={isReadOnly ? 'font-bold' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.customerRef')}</Label>
                <SearchableSelect
                  options={customerOptions}
                  value={voucher.customerId ? String(voucher.customerId) : ''}
                  onValueChange={(value) => setVoucher((prev) => ({ ...prev, customerId: Number(value) || 0 }))}
                  placeholder={t('generalEntries.selectCustomer')}
                  disabled={voucher.refType !== 'Customer' || isReadOnly}
                  className={isReadOnly ? 'font-bold' : ''}
                />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.vendorRef')}</Label>
                <SearchableSelect
                  options={vendorOptions}
                  value={voucher.vendorId ? String(voucher.vendorId) : ''}
                  onValueChange={(value) => setVoucher((prev) => ({ ...prev, vendorId: Number(value) || 0 }))}
                  placeholder={t('generalEntries.selectVendor')}
                  disabled={voucher.refType !== 'Vendor' || isReadOnly}
                  className={isReadOnly ? 'font-bold' : ''}
                />
            </div>
            <div className="space-y-2">
              <Label>{t('generalEntries.file')}</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  onChange={handleFileChange}
                  className="flex-1"
                  id="file-upload"
                  style={{ display: 'none' }}
                />
                <Input
                  value={voucher.file || t('generalEntries.chooseFile')}
                  readOnly
                  className={`flex-1 bg-muted ${isReadOnly ? 'font-bold' : ''}`}
                  onClick={!isReadOnly ? () => document.getElementById('file-upload')?.click() : undefined}
                />
                {!isReadOnly && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    type="button"
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    {t('generalEntries.browse')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Section */}
      <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
            <CardTitle>{t('generalEntries.transaction')}</CardTitle>
            {!isReadOnly && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleAddRows(1)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('generalEntries.addRow')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleAddRows(3)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('generalEntries.add3Rows')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleAddRows(5)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('generalEntries.add5Rows')}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto w-full">
            <table className="w-full border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-primary/10 border-b-2 border-primary/20">
                  <th className="p-3 text-left font-bold text-primary min-w-[200px]">{t('generalEntries.accountCode')}</th>
                  <th className="p-3 text-left font-bold text-primary min-w-[180px]">{t('generalEntries.accountTitle')}</th>
                  <th className="p-3 text-left font-bold text-primary min-w-[200px]">{t('generalEntries.description')}</th>
                  <th className="p-3 text-left font-bold text-primary min-w-[180px]">{t('generalEntries.project')}</th>
                  <th className="p-3 text-left font-bold text-primary min-w-[150px]">{t('generalEntries.costCenter')}</th>
                  <th className="p-3 text-left font-bold text-primary min-w-[150px]">{t('generalEntries.location')}</th>
                  <th className="p-3 text-right font-bold text-primary min-w-[120px]">{t('generalEntries.debit')}</th>
                  <th className="p-3 text-right font-bold text-primary min-w-[120px]">{t('generalEntries.credit')}</th>
                  <th className="p-3 text-center font-bold text-primary min-w-[80px]">{t('generalEntries.action')}</th>
                </tr>
              </thead>
              <tbody>
                {voucher.voucherLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-primary/5 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 min-w-[200px]">
                        <SearchableSelect
                          options={accountOptions}
                          value={line.accountId ? String(line.accountId) : ''}
                          onValueChange={(value) => handleLineChange(index, 'accountId', Number(value) || 0)}
                          placeholder={t('generalEntries.selectAccount')}
                          className={`w-full min-w-[200px] ${isReadOnly ? 'font-bold' : ''}`}
                          disabled={isReadOnly || isDeleted || saving}
                        />
                      </td>
                      <td className="p-3 min-w-[180px]">
                        <Input
                          value={getAccountTitle(line.accountId)}
                          readOnly
                          className={`bg-muted w-full min-w-[180px] ${isReadOnly ? 'font-bold' : ''}`}
                          placeholder={t('generalEntries.accountTitle')}
                        />
                      </td>
                      <td className="p-3 min-w-[200px]">
                        <Input
                          value={line.description}
                          onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                          placeholder={t('generalEntries.description')}
                          className={`w-full min-w-[200px] ${isReadOnly ? 'font-bold' : ''}`}
                          readOnly={isReadOnly || isDeleted}
                          disabled={saving || isDeleted}
                        />
                      </td>
                      <td className="p-3 min-w-[180px]">
                        <SearchableSelect
                          options={projectOptions}
                          value={line.projectId ? String(line.projectId) : ''}
                          onValueChange={(value) => handleLineChange(index, 'projectId', Number(value) || 0)}
                          placeholder={t('generalEntries.selectProject')}
                          className={`w-full min-w-[180px] ${isReadOnly ? 'font-bold' : ''}`}
                          disabled={isReadOnly || isDeleted || saving}
                        />
                      </td>
                      <td className="p-3 min-w-[150px]">
                        <SearchableSelect
                          options={profitCenterOptions}
                          value={line.costProfitCenterId ? String(line.costProfitCenterId) : ''}
                          onValueChange={(value) => handleLineChange(index, 'costProfitCenterId', Number(value) || 0)}
                          placeholder={t('generalEntries.profitCenter')}
                          className={`w-full min-w-[150px] ${isReadOnly ? 'font-bold' : ''}`}
                          disabled={isReadOnly || isDeleted || saving}
                        />
                      </td>
                      <td className="p-3 min-w-[150px]">
                        <SearchableSelect
                          options={costLocationOptions}
                          value={line.costLocationId ? String(line.costLocationId) : ''}
                          onValueChange={(value) => handleLineChange(index, 'costLocationId', Number(value) || 0)}
                          placeholder={t('generalEntries.costLocation')}
                          className={`w-full min-w-[150px] ${isReadOnly ? 'font-bold' : ''}`}
                          disabled={isReadOnly || isDeleted || saving}
                        />
                      </td>
                      <td className="p-3 min-w-[120px]">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.debit || ''}
                          onChange={(e) => handleLineChange(index, 'debit', Number(e.target.value) || 0)}
                          className={`text-right w-full min-w-[120px] ${isReadOnly ? 'font-bold' : ''}`}
                          placeholder="0.00"
                          readOnly={isReadOnly || isDeleted}
                          disabled={saving || isDeleted}
                        />
                      </td>
                      <td className="p-3 min-w-[120px]">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.credit || ''}
                          onChange={(e) => handleLineChange(index, 'credit', Number(e.target.value) || 0)}
                          className={`text-right w-full min-w-[120px] ${isReadOnly ? 'font-bold' : ''}`}
                          placeholder="0.00"
                          readOnly={isReadOnly || isDeleted}
                          disabled={saving || isDeleted}
                        />
                      </td>
                      <td className="p-3 text-center min-w-[80px]">
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRow(index)}
                            disabled={saving}
                            className={
                              isDeleted
                                ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                            }
                            title={isDeleted ? 'Undo delete' : 'Delete row'}
                          >
                            {isDeleted ? (
                              <span className="text-xs font-semibold">Undo</span>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
                <tr className="bg-primary/10 font-bold">
                  <td colSpan={6} className="p-3 text-right">{t('generalEntries.total')}</td>
                  <td className="p-3 text-right">{totals.totalDebit.toFixed(2)}</td>
                  <td className="p-3 text-right">{totals.totalCredit.toFixed(2)}</td>
                  <td className="p-3"></td>
                </tr>
                <tr className="bg-primary/5 font-bold">
                  <td colSpan={6} className="p-3 text-right">{t('generalEntries.difference')}</td>
                  <td colSpan={2} className="p-3 text-right">
                    {totals.difference !== 0 && (
                      <span className={totals.difference > 0 ? 'text-red-500' : 'text-green-500'}>
                        {totals.difference.toFixed(2)}
                      </span>
                    )}
                    {totals.difference === 0 && <span className="text-green-500">0</span>}
                  </td>
                  <td className="p-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      {!isReadOnly && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate('/general-entries')}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid || saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('generalEntries.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditMode ? t('generalEntries.updateGeneralEntry') : t('generalEntries.saveGeneralEntry')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

